import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  AlertTriangle, AlertCircle, Search, Clock, CheckCircle2, XCircle, 
  RefreshCw, Filter, SlidersHorizontal, Database, Zap, Wrench, Download,
  Server, Calendar, Info, Tag
} from 'lucide-react';
import { formatDate, formatDuration, SEVERITY_COLORS, SEVERITY_LABELS } from '@/lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function RefreshIndicator({ lastUpdate, interval }) {
  const formatInterval = (secs) => {
    if (secs >= 60) return `${Math.floor(secs / 60)} min`;
    return `${secs}s`;
  };
  
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <RefreshCw className="h-3 w-3" />
      <span>Auto: {formatInterval(interval)}</span>
      {lastUpdate && (
        <span className="text-muted-foreground/60">• Último: {new Date(lastUpdate).toLocaleTimeString('pt-BR')}</span>
      )}
    </div>
  );
}

function SeverityBadge({ severity }) {
  return (
    <Badge 
      className="text-white text-xs"
      style={{ backgroundColor: SEVERITY_COLORS[severity] }}
    >
      {SEVERITY_LABELS[severity]}
    </Badge>
  );
}

function StatsCard({ title, value, icon: Icon, color }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
          </div>
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Time period options in days (matching Zabbix's "Idade menor que X dias")
const TIME_PERIODS = [
  { value: '0', label: 'Todos', days: 0 },
  { value: '1', label: 'Último dia', days: 1 },
  { value: '7', label: 'Últimos 7 dias', days: 7 },
  { value: '14', label: 'Últimos 14 dias', days: 14 },
  { value: '30', label: 'Últimos 30 dias', days: 30 },
  { value: '60', label: 'Últimos 60 dias', days: 60 },
  { value: '90', label: 'Últimos 90 dias', days: 90 },
];

// Severity levels for filtering
const SEVERITY_OPTIONS = [
  { value: 5, label: 'Disaster', color: SEVERITY_COLORS[5] },
  { value: 4, label: 'High', color: SEVERITY_COLORS[4] },
  { value: 3, label: 'Average', color: SEVERITY_COLORS[3] },
  { value: 2, label: 'Warning', color: SEVERITY_COLORS[2] },
  { value: 1, label: 'Information', color: SEVERITY_COLORS[1] },
  { value: 0, label: 'Not classified', color: SEVERITY_COLORS[0] },
];

export function IncidentsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [lastUpdate, setLastUpdate] = useState(null);
  // Filter states - default to "Todos" (all time)
  const [timePeriod, setTimePeriod] = useState('0'); // 0 = all (default)
  const [selectedSeverities, setSelectedSeverities] = useState([5, 4, 3, 2, 1, 0]); // All selected by default
  const [showAcknowledged, setShowAcknowledged] = useState(true);
  const [showUnacknowledged, setShowUnacknowledged] = useState(true);
  const [showSuppressed, setShowSuppressed] = useState(false); // Default: hide suppressed (like Zabbix)
  const [queryMode, setQueryMode] = useState('triggers'); // 'problems' or 'triggers' - default triggers like Grafana
  const [includeMaintenance, setIncludeMaintenance] = useState(false); // Exclude hosts in maintenance by default (like Grafana)
  // Modal state
  const [selectedIncident, setSelectedIncident] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const { getAuthHeader } = useAuthStore();
  const { refreshInterval } = useThemeStore();

  // Apply saved filter from sessionStorage (Filtros Salvos integration)
  useEffect(() => {
    try {
      const savedFilter = sessionStorage.getItem('appliedFilter');
      if (savedFilter) {
        const f = JSON.parse(savedFilter);
        if (f.search) setSearchTerm(f.search);
        if (f.severities) setSelectedSeverities(f.severities.map(Number));
        if (f.showAcknowledged !== undefined) setShowAcknowledged(f.showAcknowledged);
        if (f.showUnacknowledged !== undefined) setShowUnacknowledged(f.showUnacknowledged);
        if (f.showSuppressed !== undefined) setShowSuppressed(f.showSuppressed);
        if (f.queryMode) setQueryMode(f.queryMode);
        if (f.timePeriod) setTimePeriod(f.timePeriod);
        sessionStorage.removeItem('appliedFilter');
      }
    } catch (err) {
      console.warn('Filtro salvo inválido, ignorando:', err);
      sessionStorage.removeItem('appliedFilter');
    }
  }, []);

  const handleIncidentClick = (incident) => {
    setSelectedIncident(incident);
    setModalOpen(true);
  };

  const fetchData = useCallback(async () => {
    try {
      // Build query params based on mode
      const params = new URLSearchParams();
      if (queryMode === 'triggers') {
        params.set('use_triggers', 'true');
        params.set('include_maintenance', includeMaintenance.toString());
      }
      params.set('include_suppressed', showSuppressed.toString());
      
      const response = await axios.get(`${API}/incidents?${params.toString()}`, { headers: getAuthHeader() });
      setData(response.data);
      setLastUpdate(new Date().toISOString());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar incidentes');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader, queryMode, includeMaintenance, showSuppressed]);

  useEffect(() => {
    setLoading(true);
    fetchData();
    const interval = setInterval(fetchData, refreshInterval * 1000);
    return () => clearInterval(interval);
  }, [fetchData, refreshInterval]);

  // Toggle severity filter
  const toggleSeverity = (severity) => {
    setSelectedSeverities(prev => 
      prev.includes(severity) 
        ? prev.filter(s => s !== severity)
        : [...prev, severity]
    );
  };

  // Apply all filters
  const filteredIncidents = data?.incidents?.filter(incident => {
    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const matchesSearch = 
        incident.name.toLowerCase().includes(term) ||
        incident.host_name.toLowerCase().includes(term) ||
        incident.trigger.toLowerCase().includes(term);
      if (!matchesSearch) return false;
    }
    
    // Severity filter
    if (!selectedSeverities.includes(incident.severity)) {
      return false;
    }
    
    // Acknowledged filter
    if (incident.acknowledged && !showAcknowledged) return false;
    if (!incident.acknowledged && !showUnacknowledged) return false;
    
    // Suppressed filter (like Zabbix's "Exibir incidentes suprimidos")
    if (incident.suppressed && !showSuppressed) return false;
    
    // Time period filter (in days - like Zabbix's "Idade menor que X dias")
    if (timePeriod !== '0') {
      const days = parseInt(timePeriod);
      const cutoffTime = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      const incidentTime = new Date(incident.timestamp);
      if (incidentTime < cutoffTime) return false;
    }
    
    return true;
  }) || [];

  // Calculate filtered stats for cards - these update when filters change
  const filteredStats = {
    total: filteredIncidents.length,
    disaster: filteredIncidents.filter(i => i.severity === 5).length,
    high: filteredIncidents.filter(i => i.severity === 4).length,
    average: filteredIncidents.filter(i => i.severity === 3).length,
    warning: filteredIncidents.filter(i => i.severity === 2).length,
    information: filteredIncidents.filter(i => i.severity === 1).length,
    not_classified: filteredIncidents.filter(i => i.severity === 0).length,
  };

  // Count active filters
  const activeFilterCount = () => {
    let count = 0;
    if (timePeriod !== '0') count++;
    if (selectedSeverities.length < 6) count++;
    if (!showAcknowledged || !showUnacknowledged) count++;
    if (showSuppressed) count++;
    return count;
  };

  // Export to CSV function
  const exportToCSV = () => {
    if (!filteredIncidents.length) return;
    
    const headers = ['Severidade', 'Host', 'Problema', 'Trigger', 'Início', 'Duração', 'ACK'];
    const rows = filteredIncidents.map(inc => [
      inc.severity_label,
      inc.host_name,
      `"${inc.name.replace(/"/g, '""')}"`,
      `"${inc.trigger.replace(/"/g, '""')}"`,
      inc.timestamp ? new Date(inc.timestamp).toLocaleString('pt-BR') : '-',
      inc.duration,
      inc.acknowledged ? 'Sim' : 'Não'
    ]);
    
    const csvContent = [
      headers.join(';'),
      ...rows.map(row => row.join(';'))
    ].join('\n');
    
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `incidentes_ativos_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}><CardContent className="p-4"><Skeleton className="h-16 w-full" /></CardContent></Card>
          ))}
        </div>
        <Skeleton className="h-[500px] w-full" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full space-y-6" data-testid="incidents-page">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Header with Refresh and Filters */}
      <div className="flex items-center justify-between flex-wrap gap-4 flex-shrink-0">
        <div className="flex items-center gap-4 flex-wrap">
          {/* Query Mode Toggle - Triggers (like Grafana) vs Problems */}
          <Tabs value={queryMode} onValueChange={setQueryMode} className="w-auto">
            <TabsList className="h-9">
              <TabsTrigger value="triggers" className="text-xs gap-1" data-testid="mode-triggers">
                <Zap className="h-3 w-3" />
                Triggers
              </TabsTrigger>
              <TabsTrigger value="problems" className="text-xs gap-1" data-testid="mode-problems">
                <Database className="h-3 w-3" />
                Problems
              </TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Time Period Filter - like Zabbix's "Idade menor que X dias" */}
          <Select value={timePeriod} onValueChange={setTimePeriod}>
            <SelectTrigger className="w-[180px]" data-testid="time-period-filter">
              <Clock className="h-4 w-4 mr-2" />
              <SelectValue placeholder="Idade menor que" />
            </SelectTrigger>
            <SelectContent>
              {TIME_PERIODS.map(p => (
                <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Severity Filter Popover */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative" data-testid="severity-filter-btn">
                <SlidersHorizontal className="h-4 w-4 mr-2" />
                Severidade
                {selectedSeverities.length < 6 && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-primary text-[10px]">
                    {selectedSeverities.length}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64">
              <div className="space-y-3">
                <p className="text-sm font-medium">Filtrar por Severidade</p>
                {SEVERITY_OPTIONS.map(sev => (
                  <div key={sev.value} className="flex items-center space-x-3">
                    <Checkbox
                      id={`sev-${sev.value}`}
                      checked={selectedSeverities.includes(sev.value)}
                      onCheckedChange={() => toggleSeverity(sev.value)}
                    />
                    <Label 
                      htmlFor={`sev-${sev.value}`} 
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <span 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: sev.color }}
                      />
                      {sev.label}
                    </Label>
                  </div>
                ))}
                <div className="pt-2 border-t flex justify-between">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedSeverities([5, 4, 3, 2, 1, 0])}
                  >
                    Todos
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedSeverities([5, 4, 3])}
                  >
                    Críticos
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* More Filters Popover (ACK + Suppressed) */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="relative" data-testid="more-filters-btn">
                <Filter className="h-4 w-4 mr-2" />
                Mais Filtros
                {((!showAcknowledged || !showUnacknowledged) || showSuppressed) && (
                  <Badge className="ml-2 h-5 w-5 p-0 flex items-center justify-center bg-primary text-[10px]">
                    !
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-72">
              <div className="space-y-4">
                <div>
                  <p className="text-sm font-medium mb-3">Status de Confirmação</p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="ack-yes"
                        checked={showAcknowledged}
                        onCheckedChange={setShowAcknowledged}
                      />
                      <Label htmlFor="ack-yes" className="flex items-center gap-2 cursor-pointer">
                        <CheckCircle2 className="h-4 w-4 text-green-500" />
                        Reconhecido (ACK)
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="ack-no"
                        checked={showUnacknowledged}
                        onCheckedChange={setShowUnacknowledged}
                      />
                      <Label htmlFor="ack-no" className="flex items-center gap-2 cursor-pointer">
                        <XCircle className="h-4 w-4 text-muted-foreground" />
                        Não Reconhecido
                      </Label>
                    </div>
                  </div>
                </div>
                <div className="pt-3 border-t space-y-3">
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id="show-suppressed"
                      checked={showSuppressed}
                      onCheckedChange={setShowSuppressed}
                    />
                    <Label htmlFor="show-suppressed" className="cursor-pointer text-sm">
                      Exibir incidentes suprimidos
                    </Label>
                  </div>
                  {queryMode === 'triggers' && (
                    <div className="flex items-center space-x-3">
                      <Checkbox
                        id="include-maintenance"
                        checked={includeMaintenance}
                        onCheckedChange={setIncludeMaintenance}
                      />
                      <Label htmlFor="include-maintenance" className="flex items-center gap-2 cursor-pointer text-sm">
                        <Wrench className="h-3 w-3" />
                        Incluir hosts em manutenção
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>

          {/* Export CSV Button */}
          <Button variant="outline" onClick={exportToCSV} disabled={!filteredIncidents.length}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>

          {/* Active Filters Badge */}
          {activeFilterCount() > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFilterCount()} filtro{activeFilterCount() > 1 ? 's' : ''} ativo{activeFilterCount() > 1 ? 's' : ''}
            </Badge>
          )}
        </div>

        <RefreshIndicator lastUpdate={lastUpdate} interval={refreshInterval} />
      </div>

      {/* Stats Cards - now show FILTERED values */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 flex-shrink-0">
        <StatsCard 
          title="Total" 
          value={filteredStats.total} 
          icon={AlertTriangle} 
          color="bg-gray-500"
        />
        <StatsCard 
          title="Disaster" 
          value={filteredStats.disaster} 
          icon={XCircle} 
          color="bg-red-700"
        />
        <StatsCard 
          title="High" 
          value={filteredStats.high} 
          icon={AlertTriangle} 
          color="bg-red-500"
        />
        <StatsCard 
          title="Average" 
          value={filteredStats.average} 
          icon={AlertCircle} 
          color="bg-orange-500"
        />
        <StatsCard 
          title="Warning" 
          value={filteredStats.warning} 
          icon={AlertCircle} 
          color="bg-yellow-500"
        />
        <StatsCard 
          title="Info" 
          value={filteredStats.information} 
          icon={AlertCircle} 
          color="bg-blue-500"
        />
      </div>

      {/* Search and Table */}
      <Card className="flex-1 flex flex-col min-h-0">
        <CardHeader className="pb-3 flex-shrink-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Alertas Ativos
              <Badge variant="outline" className="ml-2 font-mono">
                Exibindo {filteredIncidents.length} de {data?.total || 0}
              </Badge>
            </CardTitle>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar incidentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-incidents"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0 flex-1 min-h-0">
          <ScrollArea className="h-full" style={{ height: 'calc(100vh - 350px)', minHeight: '400px' }}>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Severidade</TableHead>
                  <TableHead>Host</TableHead>
                  <TableHead>Problema</TableHead>
                  <TableHead className="w-[150px] whitespace-nowrap">Duração</TableHead>
                  <TableHead className="w-[160px]">Início</TableHead>
                  <TableHead className="w-[80px] text-center">ACK</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIncidents.length > 0 ? (
                  filteredIncidents.map((incident) => (
                    <TableRow 
                      key={incident.id}
                      className={`cursor-pointer hover:bg-muted/50 ${incident.severity >= 4 ? 'bg-red-500/10' : incident.severity >= 3 ? 'bg-orange-500/10' : ''}`}
                      data-testid={`incident-row-${incident.id}`}
                      onClick={() => handleIncidentClick(incident)}
                    >
                      <TableCell>
                        <SeverityBadge severity={incident.severity} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{incident.host_name}</TableCell>
                      <TableCell>
                        <div className="max-w-md">
                          <p className="font-medium text-sm truncate">{incident.name}</p>
                          {incident.opdata && (
                            <p className="text-xs text-muted-foreground truncate">{incident.opdata}</p>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3 w-3 flex-shrink-0" />
                          <span className="whitespace-nowrap">{formatDuration(incident.duration)}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(incident.timestamp)}
                      </TableCell>
                      <TableCell className="text-center">
                        {incident.acknowledged ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500 mx-auto" />
                        ) : (
                          <XCircle className="h-4 w-4 text-muted-foreground mx-auto" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-12 text-muted-foreground">
                      {searchTerm ? 'Nenhum incidente encontrado' : 'Nenhum incidente ativo no momento'}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </ScrollArea>
        </CardContent>
      </Card>

      {/* Incident Detail Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Detalhes do Incidente
            </DialogTitle>
            <DialogDescription>
              Informações completas do evento/problema
            </DialogDescription>
          </DialogHeader>
          
          {selectedIncident && (
            <div className="space-y-4 mt-4">
              {/* Severity and Status */}
              <div className="flex items-center gap-4">
                <SeverityBadge severity={selectedIncident.severity} />
                {selectedIncident.acknowledged ? (
                  <Badge className="bg-green-500 text-white">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Reconhecido
                  </Badge>
                ) : (
                  <Badge variant="secondary">
                    <XCircle className="h-3 w-3 mr-1" />
                    Não Reconhecido
                  </Badge>
                )}
                {selectedIncident.suppressed && (
                  <Badge variant="outline">Suprimido</Badge>
                )}
              </div>

              {/* Problem Name */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    Problema
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-medium">{selectedIncident.name}</p>
                  {selectedIncident.opdata && (
                    <p className="text-sm text-muted-foreground mt-1">{selectedIncident.opdata}</p>
                  )}
                </CardContent>
              </Card>

              {/* Host Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Server className="h-4 w-4" />
                    Host
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="font-mono">{selectedIncident.host_name}</p>
                  {selectedIncident.host_id && (
                    <p className="text-xs text-muted-foreground mt-1">ID: {selectedIncident.host_id}</p>
                  )}
                </CardContent>
              </Card>

              {/* Trigger Info */}
              {selectedIncident.trigger && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Tag className="h-4 w-4" />
                      Trigger
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm">{selectedIncident.trigger}</p>
                  </CardContent>
                </Card>
              )}

              {/* Time Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Tempo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Início</p>
                      <p className="font-medium">{formatDate(selectedIncident.timestamp)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Duração</p>
                      <p className="font-medium flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatDuration(selectedIncident.duration)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Additional Info */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Info className="h-4 w-4" />
                    Informações Adicionais
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Event ID</p>
                      <p className="font-mono">{selectedIncident.id}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Severidade</p>
                      <p>{selectedIncident.severity_label}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
